/**
 * Firestore Cleanup Utility
 * Use this to delete all lessons and/or students before a fresh import
 *
 * CAUTION: This will permanently delete data!
 */

import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Delete all documents from a collection
 * @param {string} collectionName - Name of the collection to clear
 * @returns {Promise<number>} Number of documents deleted
 */
export async function clearCollection(collectionName) {
  try {
    console.log(`Starting deletion of ${collectionName} collection...`);

    const snapshot = await getDocs(collection(db, collectionName));
    const deletePromises = [];

    snapshot.forEach((docSnapshot) => {
      deletePromises.push(deleteDoc(doc(db, collectionName, docSnapshot.id)));
    });

    await Promise.all(deletePromises);

    console.log(`✓ Deleted ${deletePromises.length} documents from ${collectionName}`);
    return deletePromises.length;
  } catch (error) {
    console.error(`Error clearing ${collectionName}:`, error);
    throw error;
  }
}

/**
 * Delete all lessons
 */
export async function deleteAllLessons() {
  return clearCollection('lessons');
}

/**
 * Delete all students
 */
export async function deleteAllStudents() {
  return clearCollection('students');
}

/**
 * Delete both students and lessons (full reset)
 */
export async function fullReset() {
  console.log('⚠️  FULL RESET: Deleting all students and lessons...');

  const lessonsDeleted = await deleteAllLessons();
  const studentsDeleted = await deleteAllStudents();

  console.log(`\n✓ Full reset complete!`);
  console.log(`  - Students deleted: ${studentsDeleted}`);
  console.log(`  - Lessons deleted: ${lessonsDeleted}`);

  return { studentsDeleted, lessonsDeleted };
}

/**
 * Count documents in a collection
 */
export async function countDocuments(collectionName) {
  const snapshot = await getDocs(collection(db, collectionName));
  return snapshot.size;
}

/**
 * Get data summary
 */
export async function getDataSummary() {
  const studentsCount = await countDocuments('students');
  const lessonsCount = await countDocuments('lessons');

  return {
    students: studentsCount,
    lessons: lessonsCount
  };
}
